import { lazy } from "react"
import { Navigate, type RouteObject } from "react-router-dom"
import AppShell from "@/app/router/AppShell"
import { RequireAuth, RequireRole } from "@/app/auth/guards"
import { RequireWorkspace } from "@/app/workspaces/RequireWorkspace"
import { CARD_TYPES } from "@/shared/api/types"
import { PreserveSearchRedirect } from "@/app/router/PreserveSearchRedirect"

const HomePage = lazy(() => import("@/pages/home/page"))
const CatalogLayout = lazy(() => import("@/pages/catalog/layout"))
const SearchPage = lazy(() => import("@/pages/search/page"))
const EntityPage = lazy(() => import("@/pages/entity/page"))
const NewsPage = lazy(() => import("@/pages/news/page"))
const AboutPage = lazy(() => import("@/pages/about/page"))
const ContactsPage = lazy(() => import("@/pages/contacts/page"))
const AgreementPage = lazy(() => import("@/pages/legal/AgreementPage"))
const PersonalDataConsentPage = lazy(() => import("@/pages/legal/PersonalDataConsentPage"))
const PolicyPrivacyPage = lazy(() => import("@/pages/legal/PolicyPrivacyPage"))
const AccountPage = lazy(() => import("@/pages/account/page"))
const NotificationsPage = lazy(() => import("@/pages/account/notifications"))
const QueryPage = lazy(() => import("@/pages/query/page"))
const LoginPage = lazy(() => import("@/pages/auth/login"))
const RegisterPage = lazy(() => import("@/pages/auth/register"))
const ForgotPasswordPage = lazy(() => import("@/pages/auth/forgot-password"))
const ResetPasswordPage = lazy(() => import("@/pages/auth/reset-password"))
const NotFoundPage = lazy(() => import("@/pages/not-found/page"))
const AdminLayout = lazy(() => import("@/pages/admin/layout"))
const AdminOverviewPage = lazy(() => import("@/pages/admin/overview"))
const AdminEntitiesPage = lazy(() => import("@/pages/admin/entities/page"))
const AdminNbrOldPage = lazy(() => import("@/pages/admin/nbr-old/page"))
const AdminEntityEditPage = lazy(() => import("@/pages/admin/entities/edit"))
const AdminMergePage = lazy(() => import("@/pages/admin/merge/page"))
const AdminErrorsPage = lazy(() => import("@/pages/admin/errors/page"))
const AdminDictionariesPage = lazy(() => import("@/pages/admin/dictionaries/page"))
const AdminImportPage = lazy(() => import("@/pages/admin/import/page"))
const AdminAuditPage = lazy(() => import("@/pages/admin/audit/page"))
const AdminUsersPage = lazy(() => import("@/pages/admin/users/page"))
const AdminAssociationsPage = lazy(() => import("@/pages/admin/associations/page"))
const AdminCreativePage = lazy(() => import("@/pages/admin/creative/page"))
const AdminEventAuditPage = lazy(() => import("@/pages/admin/audit/events"))
const AdminDepositAuditPage = lazy(() => import("@/pages/admin/audit/deposits"))
const EventsPage = lazy(() => import("@/pages/events/page"))
const EventDetailPage = lazy(() => import("@/pages/events/EventDetailPage"))
const DepositsPage = lazy(() => import("@/pages/deposits/page"))
const DepositDetailPage = lazy(() => import("@/pages/deposits/DepositDetailPage"))
const EventsPortalLayout = lazy(() => import("@/pages/events/portal/layout"))
const EventsPortalOverviewPage = lazy(() => import("@/pages/events/portal/overview"))
const EventsPortalAllPage = lazy(() => import("@/pages/events/portal/all"))
const EventsPortalMinePage = lazy(() => import("@/pages/events/portal/mine"))
const EventsPortalCreatePage = lazy(() => import("@/pages/events/portal/create"))
const EventsPortalModerationPage = lazy(() => import("@/pages/events/portal/moderation"))
const EventsPortalRegistrationsPage = lazy(() => import("@/pages/events/portal/registrations"))
const EventsPortalAttendancePage = lazy(() => import("@/pages/events/portal/attendance"))
const DepositPortalLayout = lazy(() => import("@/pages/deposit/portal/layout"))
const DepositPortalOverviewPage = lazy(() => import("@/pages/deposit/portal/overview"))
const DepositPortalRegistryPage = lazy(() => import("@/pages/deposit/portal/registry"))
const DepositPortalMyPage = lazy(() => import("@/pages/deposit/portal/my"))
const DepositPortalApplyPage = lazy(() => import("@/pages/deposit/portal/apply"))
const DepositPortalModerationPage = lazy(() => import("@/pages/deposit/portal/moderation"))
const WriterLayout = lazy(() => import("@/pages/writer/layout"))
const WriterOverviewPage = lazy(() => import("@/pages/writer/overview"))
const WriterProfilePage = lazy(() => import("@/pages/writer/profile"))
const WriterPortfolioPage = lazy(() => import("@/pages/writer/portfolio"))
const WriterAwardsPage = lazy(() => import("@/pages/writer/awards"))
const WriterApplicationPage = lazy(() => import("@/pages/writer/application"))
const WriterDepositsPage = lazy(() => import("@/pages/writer/deposits"))
const WriterEventsPage = lazy(() => import("@/pages/writer/events"))
const UnionLayout = lazy(() => import("@/pages/union/layout"))
const UnionOverviewPage = lazy(() => import("@/pages/union/overview"))
const UnionApplicationsPage = lazy(() => import("@/pages/union/applications"))
const UnionMembersPage = lazy(() => import("@/pages/union/members"))
const UnionWorksPage = lazy(() => import("@/pages/union/works"))
const UnionEventsPage = lazy(() => import("@/pages/union/events"))
const UnionStructurePage = lazy(() => import("@/pages/union/structure"))
const UnionAuthorPage = lazy(() => import("@/pages/union/author"))
const ClaimWriterPage = lazy(() => import("@/pages/union/claim"))
const WritersRegistryPage = lazy(() => import("@/pages/writers/page"))
const WriterPublicDetailPage = lazy(() => import("@/pages/writers/detail"))
const AnalyticsPage = lazy(() => import("@/pages/analytics/page"))
const CabinetSummaryPage = lazy(() => import("@/pages/cabinet/summary"))

const entityChildren: RouteObject[] = CARD_TYPES.map((type) => ({
  path: `${type}/:id`,
  element: <EntityPage />,
}))

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <HomePage /> },
      {
        path: "catalog",
        element: <CatalogLayout />,
        children: [
          { index: true, element: <PreserveSearchRedirect to="/catalog/search" /> },
          { path: "search", element: <SearchPage /> },
          { path: "query", element: <QueryPage /> },
        ],
      },
      { path: "search", element: <PreserveSearchRedirect to="/catalog/search" /> },
      { path: "query", element: <PreserveSearchRedirect to="/catalog/query" /> },
      { path: "news", element: <NewsPage /> },
      { path: "about", element: <AboutPage /> },
      { path: "contacts", element: <ContactsPage /> },
      { path: "help/agreement", element: <AgreementPage /> },
      { path: "help/personal", element: <PersonalDataConsentPage /> },
      { path: "help/privacy", element: <PolicyPrivacyPage /> },
      { path: "profile", element: <Navigate to="/account" replace /> },
      { path: "events", element: <EventsPage /> },
      { path: "events/:id", element: <EventDetailPage /> },
      { path: "deposits", element: <DepositsPage /> },
      { path: "deposits/:id", element: <DepositDetailPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "register", element: <RegisterPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { path: "reset-password", element: <ResetPasswordPage /> },
      { path: "writers", element: <WritersRegistryPage /> },
      { path: "writers/:slug", element: <WriterPublicDetailPage /> },
      ...entityChildren,
      { path: "cabinet", element: <CabinetSummaryPage /> },
      { path: "account", element: <AccountPage /> },
      { path: "account/collections", element: <AccountPage /> },
      { path: "account/profile", element: <AccountPage /> },
      { path: "account/notifications", element: <NotificationsPage /> },
      { path: "claim", element: <ClaimWriterPage /> },
      {
        path: "writer",
        element: (
          <RequireWorkspace id="writer">
            <WriterLayout />
          </RequireWorkspace>
        ),
        children: [
          { index: true, element: <WriterOverviewPage /> },
          { path: "profile", element: <WriterProfilePage /> },
          { path: "portfolio", element: <WriterPortfolioPage /> },
          { path: "awards", element: <WriterAwardsPage /> },
          { path: "application", element: <WriterApplicationPage /> },
          { path: "deposits", element: <WriterDepositsPage /> },
          { path: "events", element: <WriterEventsPage /> },
        ],
      },
      {
        path: "union",
        element: (
          <RequireWorkspace id="union">
            <UnionLayout />
          </RequireWorkspace>
        ),
        children: [
          { index: true, element: <UnionOverviewPage /> },
          { path: "applications", element: <UnionApplicationsPage /> },
          { path: "members", element: <UnionMembersPage /> },
          { path: "authors", element: <UnionAuthorPage /> },
          { path: "works", element: <UnionWorksPage /> },
          { path: "events", element: <UnionEventsPage /> },
          { path: "structure", element: <UnionStructurePage /> },
        ],
      },
      {
        path: "analytics",
        element: (
          <RequireWorkspace id="catalog">
            <AnalyticsPage />
          </RequireWorkspace>
        ),
      },
      {
        path: "portal/events",
        element: <EventsPortalLayout />,
        children: [
          { index: true, element: <EventsPortalOverviewPage /> },
          { path: "all", element: <EventsPortalAllPage /> },
          { path: "created", element: <EventsPortalMinePage /> },
          { path: "create", element: <EventsPortalCreatePage /> },
          { path: "moderation", element: <EventsPortalModerationPage /> },
          { path: "registrations", element: <EventsPortalRegistrationsPage /> },
          { path: "attendance", element: <EventsPortalAttendancePage /> },
        ],
      },
      {
        path: "portal/deposit",
        element: <DepositPortalLayout />,
        children: [
          { index: true, element: <DepositPortalOverviewPage /> },
          { path: "registry", element: <DepositPortalRegistryPage /> },
          { path: "registry/:id", element: <DepositDetailPage /> },
          { path: "my", element: <DepositPortalMyPage /> },
          { path: "my/:id", element: <DepositDetailPage /> },
          { path: "apply", element: <DepositPortalApplyPage /> },
          { path: "moderation", element: <DepositPortalModerationPage /> },
          { path: "moderation/:id", element: <DepositDetailPage /> },
        ],
      },
      {
        path: "admin",
        element: (
          <RequireRole role={["librarian", "admin"]}>
            <AdminLayout />
          </RequireRole>
        ),
        children: [
          { index: true, element: <AdminOverviewPage /> },
          {
            path: "users",
            element: (
              <RequireRole role="admin">
                <AdminUsersPage />
              </RequireRole>
            ),
          },
          {
            path: "roles",
            element: (
              <RequireRole role="admin">
                <AdminUsersPage />
              </RequireRole>
            ),
          },
          { path: "creative", element: <AdminCreativePage /> },
          { path: "entities", element: <AdminEntitiesPage /> },
          { path: "nbr-old", element: <AdminNbrOldPage /> },
          { path: "entities/new", element: <AdminEntityEditPage /> },
          { path: "entities/:id", element: <AdminEntityEditPage /> },
          { path: "merge", element: <AdminMergePage /> },
          { path: "errors", element: <AdminErrorsPage /> },
          { path: "dictionaries", element: <AdminDictionariesPage /> },
          { path: "import", element: <AdminImportPage /> },
          { path: "audit", element: <AdminAuditPage /> },
          { path: "associations", element: <AdminAssociationsPage /> },
          { path: "audit/events", element: <AdminEventAuditPage /> },
          { path: "audit/deposits", element: <AdminDepositAuditPage /> },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]

export default routes
